import { reaction } from "mobx";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useRef } from "react";

import Messages from "@/components/Playground/Messages";
import { PlaygroundStoreProvider, usePlayground } from "@/components/Playground/playground-store";
import { PlaygroundDialogStoreProvider } from "@/components/Playground/PlaygroundDialog/playground-dialog-store";
import { InlineTemplate } from "@/types/evaluate";

// Syncs Playground changes back to the parent using MobX reaction
const PlaygroundChangeSync = ({
  template,
  onTemplateChange,
}: {
  template?: InlineTemplate;
  onTemplateChange: (template: InlineTemplate) => void;
}) => {
  const playground = usePlayground();
  const templateRef = useRef(template);
  const onTemplateChangeRef = useRef(onTemplateChange);

  templateRef.current = template;
  onTemplateChangeRef.current = onTemplateChange;

  useEffect(() => {
    const dispose = reaction(
      () =>
        JSON.stringify({
          prompt_template: playground.blueprint?.prompt_template,
          metadata: playground.blueprint?.metadata,
        }),
      () => {
        if (playground.blueprint?.prompt_template) {
          onTemplateChangeRef.current({
            ...templateRef.current,
            prompt_template: playground.blueprint.prompt_template,
            metadata: playground.blueprint.metadata,
          } as InlineTemplate);
        }
      },
      { fireImmediately: true },
    );

    return () => dispose();
  }, [playground]);

  return null;
};

// Inline editor using Playground's Messages component
const InlinePlaygroundEditor = observer(
  ({
    template,
    onTemplateChange,
  }: {
    template?: InlineTemplate;
    onTemplateChange: (template: InlineTemplate) => void;
  }) => {
    const playground = usePlayground();

    return (
      <div className="h-[clamp(360px,58vh,640px)] overflow-hidden rounded-lg border border-gray-200 bg-white">
        <Messages
          messages={playground.messages}
          onAddMessage={() => playground.addEmptyMessage()}
          title="Prompt"
        />
        <PlaygroundChangeSync template={template} onTemplateChange={onTemplateChange} />
      </div>
    );
  },
);

interface InlineEditorSectionProps {
  template?: InlineTemplate;
  onTemplateChange: (template: InlineTemplate) => void;
  readonly?: boolean;
}

// Wrapper that provides PlaygroundStoreProvider for inline mode
export const InlineEditorSection = ({
  template,
  onTemplateChange,
  readonly,
}: InlineEditorSectionProps) => {
  const initialBlueprint = useMemo(() => {
    if (!template?.prompt_template) {
      return {
        prompt_template: {
          type: "chat" as const,
          input_variables: [],
          messages: [
            {
              role: "user" as const,
              content: [{ type: "text" as const, text: "" }],
              template_format: "jinja2" as const,
            },
          ],
        },
        metadata: template?.metadata,
      };
    }

    return {
      prompt_template: template.prompt_template,
      metadata: template.metadata,
    };
  }, [template]);

  return (
    <PlaygroundStoreProvider
      key={`inline-prompt-${JSON.stringify(template?.prompt_template?.type)}`}
      value={{
        // @ts-expect-error - prompt_template type is flexible for inline templates
        prompt_blueprint: initialBlueprint,
        input_variables: [],
        readonly: readonly,
        editor: true,
        inline_editor: true,
      }}
    >
      <PlaygroundDialogStoreProvider>
        <InlinePlaygroundEditor template={template} onTemplateChange={onTemplateChange} />
      </PlaygroundDialogStoreProvider>
    </PlaygroundStoreProvider>
  );
};
